import { Data, StatisticsOptions } from '@bufdir/statistics-common';
import {
  AccordionsBlockComponent,
  AccordionsBlockProps,
  HomepageData,
  HomeProps,
  iconNames,
  InfoBlockComponent,
  InfoBlockProps,
  MainPointItem,
  MainPointProps,
  PageAttributes,
  Relation,
  SectionAttributes,
  SectionComponent,
  SectionProps,
  StatisticsBlockComponent,
  StatisticspageAttributes,
  StatisticspageData,
  StatisticspageLinkProps,
  StatisticspageProps,
  StatsBlockProps,
  SubjectAttributes,
  SubjectCategoryAttributes,
  SubjectCategoryPageLink,
  SubjectCategoryProps,
  SubjectGroup,
  SubjectGroupProps,
  SubjectLinkProps,
  SubjectProps,
  SubjectsRelation,
  SubjectSummaryAttributes,
  SubjectSummaryLinkProps,
} from '@bufdir/statistics-pages-components';

export const toMediaUrl = (url: string, mediaBase: string) => {
  if (url && mediaBase) {
    if (url.startsWith('http')) {
      return url;
    }
    if (mediaBase.length < 2) {
      return url;
    }
    if (url.startsWith('/')) {
      return `${mediaBase}${url}`;
    }
    return `${mediaBase}/${url}`;
  }
  return url;
};

const fallbackOptions = (): StatisticsOptions => ({
  dataId: '',
  sheetIndex: 0,
  title: '',
  subtitle: '',
  dimensionLabels: {},
  annotations: [],
  initialSettings: {
    primary: '',
    filters: {},
    unitsDimension: '',
  },
  unitOptions: { units: 'Percent', names: {}, labels: {} },
  rules: {
    mandatoryFilterDefaults: {},
    dimensionRestrictions: {},
    dimensionFlags: {},
    singlePrimaryFilterDefaults: {},
  },
  chartOptions: {
    overrideMaxY: {},
    isStacked: false,
    minYPercent: 100,
    horizontalBar: false,
    disableDownloadOriginalExcel: false,
  },
});

const resolveOptions = (
  component: StatisticsBlockComponent,
): StatisticsOptions => {
  if (component.block?.statisticsOptions) {
    return component.block.statisticsOptions;
  }
  if (component.block?.options) {
    return component.block.options;
  }
  if (component.block?.editorOptions) {
    try {
      const optionsFromString = JSON.parse(
        component.block.editorOptions,
      ) as StatisticsOptions;
      if (
        'initialSettings' in optionsFromString &&
        'dimensionLabels' in optionsFromString &&
        'chartOptions' in optionsFromString &&
        'rules' in optionsFromString &&
        'unitOptions' in optionsFromString
      ) {
        return optionsFromString;
      }
    } catch (err) {
      // swallow error, just another try}
    }
  }
  return fallbackOptions();
};

export const toStatsBlockProps = (
  component: StatisticsBlockComponent,
  sectionId: number,
  mediaBase: string,
): StatsBlockProps | undefined => {
  if (!component?.block) {
    return undefined;
  }
  return {
    id: component.block.id,
    parentId: sectionId,
    data: component.block.data || [],
    options: resolveOptions(component),
    type: 'statistics.statistics-block',
    content: {
      title: component.block.title,
      dataDescription: component.block.dataDescription,
      dataExplanation: component.block.dataExplanation,
      description: component.block.description,
      explanation: component.block.explanation,
    },
    file: {
      ...component.block.file,
      url: toMediaUrl(component.block.file?.url, mediaBase),
      id: component.block.file?.id,
    },
  };
};

export const toInfoBlockProps = (
  component: InfoBlockComponent,
  sectionId: number,
): InfoBlockProps => {
  return {
    id: component.id,
    parentId: sectionId,
    title: component?.title,
    body: component?.body,
    tooltips: component?.tooltips,
    isInfoBlock: component.isInfoBlock,
    type: 'statistics.info-block',
    keyContext: `section-${sectionId}-infoblock-${component.id}`,
  };
};

export const toAccordionsBlockProps = (
  component: AccordionsBlockComponent,
  sectionId: number,
): AccordionsBlockProps => {
  return {
    id: component.id,
    keyContext: `section-${sectionId}-accordionsblock-${component.id}`,
    type: 'statistics.accordions-block',
    accordions: component.accordions,
  };
};
export const toSectionComponentsProps = (
  components: SectionComponent[],
  sectionId: number,
  mediaBase: string,
): (InfoBlockProps | StatsBlockProps | AccordionsBlockProps)[] => {
  if (!components) {
    return [];
  }
  return components
    .map((component) => {
      if (component.__component === 'statistics.info-block') {
        return toInfoBlockProps(component, sectionId);
      } else {
        if (component.__component === 'statistics.statistics-block') {
          return toStatsBlockProps(component, sectionId, mediaBase);
        } else {
          return toAccordionsBlockProps(component, sectionId);
        }
      }
    })
    .filter((props) => props) as (
    | InfoBlockProps
    | StatsBlockProps
    | AccordionsBlockProps
  )[];
};

export const toSectionProps = (
  sectionData: Relation<'section', SectionAttributes> | Data<SectionAttributes>,
  mediaBase: string,
): SectionProps | undefined => {
  if (!sectionData) {
    return undefined;
  }
  if ('section' in sectionData) {
    if (sectionData.section !== null)
      return toSectionProps(sectionData.section, mediaBase);
    return undefined;
  } else {
    return {
      id: sectionData?.id,
      title: sectionData?.title,
      displayTitle: sectionData?.displayTitle,
      ingress: sectionData?.ingress,
      introduction: sectionData?.introduction,
      slug: sectionData?.slug,
      createdAt: sectionData?.createdAt,
      updatedAt: sectionData?.updatedAt,
      publishedAt: sectionData?.publishedAt,
      components: toSectionComponentsProps(
        sectionData?.components,
        sectionData?.id,
        mediaBase,
      ),
    };
  }
};

export const toSectionsProps = (
  relations: Relation<'section', SectionAttributes>[],
  mediaBase: string,
): SectionProps[] =>
  relations.map((relation) => toSectionProps(relation, mediaBase));

export const toStatisticsPageLinkProps = (
  data: SubjectCategoryPageLink,
): StatisticspageLinkProps | undefined => {
  if (!data || (!data.page && !(data.externalUrl && data.linkText))) {
    return undefined;
  }

  return {
    ingress: data?.page?.ingress || '',
    linkText: data?.linkText || '',
    externalUrl: data?.externalUrl || '',
    slug: data?.page?.slug || '',
    title: data?.page?.title || '',
    displayTitle: data?.page?.displayTitle || '',
    createdAt: data?.page?.createdAt,
    updatedAt: data?.page?.updatedAt,
    publishedAt: data?.page?.publishedAt,
  };
};

export const toStatisticspageProps = (
  pageData:
    | StatisticspageAttributes
    | StatisticspageData
    | Relation<'page', PageAttributes>,
  mediaBase: string,
): StatisticspageProps => {
  if (pageData && 'page' in pageData) {
    return toStatisticspageProps(pageData.page, mediaBase);
  }
  const attributes = pageData as StatisticspageAttributes;
  const mainPoints = attributes?.mainPoints
    ? attributes.mainPoints.map((point) => toMainPoint(point))
    : [];
  return {
    title: attributes?.title,
    displayTitle: attributes?.displayTitle,
    ingress: attributes?.ingress,
    introduction: attributes?.introduction,
    slug: attributes?.slug,
    mainPoints,
    sections: toSectionsProps(attributes?.sections || [], mediaBase),
    richtextSections: attributes?.richtextSections || [],
    createdAt: attributes?.createdAt,
    updatedAt: attributes?.updatedAt,
    publishedAt: attributes?.publishedAt,
  };
};

export const toSubjectCategoryProps = (
  categoryAttributes: SubjectCategoryAttributes,
  subjectSlug: string,
): SubjectCategoryProps => {
  const pages: StatisticspageLinkProps[] = (
    categoryAttributes.categoryPages || []
  ).map((page) => toStatisticsPageLinkProps(page));

  return {
    keyContext: `subject-category-${categoryAttributes.id}`,
    title: categoryAttributes.title,
    ingress: categoryAttributes.ingress,
    pages,
    subjectSlug,
  };
};

export const toSubjectCategoriesProps = (
  categoriesAttributes: SubjectCategoryAttributes[],
  subjectSlug: string,
): SubjectCategoryProps[] => {
  return (
    categoriesAttributes?.map((categoryAttributes) =>
      toSubjectCategoryProps(categoryAttributes, subjectSlug),
    ) || []
  );
};

export const toStatisticspagesProps = (
  pagesData:
    | StatisticspageData[]
    | Relation<'page', PageAttributes>[]
    | SubjectCategoryPageLink[],
  mediaBase: string,
) => {
  return pagesData.map((pageData) =>
    toStatisticspageProps(pageData, mediaBase),
  );
};

export const toSubjectSummaryProps = (
  summary: SubjectSummaryAttributes,
): SubjectSummaryLinkProps => {
  const fallback: SubjectSummaryLinkProps = {
    page: {
      ingress: '',
      mainPoints: [],
      title: '',
      displayTitle: '',
      sections: [],
      richtextSections: [],
      slug: '',
      updatedAt: '',
      publishedAt: null,
      createdAt: '',
    },
  };
  if (summary?.page) {
    return {
      page: toStatisticspageProps(summary?.page, ''),
      linkText: summary.linkText,
    };
  }
  return fallback;
};

export const toSubjectProps = (
  subjectData:
    | Data<SubjectAttributes>
    | (Relation<'subject', SubjectAttributes> & SubjectLinkProps),
): SubjectProps | undefined => {
  if (!subjectData) {
    return undefined;
  }
  if ('subject' in subjectData) {
    if (subjectData.subject !== null) {
      const subjectProps = toSubjectProps(subjectData.subject);
      if (subjectProps) {
        return {
          ...subjectProps,
          linkTitle: subjectData.linkTitle,
          linkDescription: subjectData.linkDescription,
          linkExternal: subjectData.linkExternal,
        };
      }
      return undefined;
    }
  } else
    return {
      id: subjectData?.id,
      description: subjectData?.description || '',
      name: subjectData?.name || '',
      shortDescription: subjectData?.shortDescription || '',
      slug: subjectData?.slug || '',
      createdAt: subjectData?.createdAt || '',
      updatedAt: subjectData?.updatedAt || '',
      publishedAt: subjectData?.publishedAt || null,
      categories: toSubjectCategoriesProps(
        subjectData?.categories || [],
        subjectData?.slug,
      ),
      summary: subjectData?.summary
        ? toSubjectSummaryProps(subjectData.summary)
        : {
            page: {
              ingress: '',
              displayTitle: '',
              sections: [],
              slug: '',
              title: '',
              mainPoints: [],
              richtextSections: [],
              updatedAt: '',
              createdAt: '',
              publishedAt: null,
            },
          },
    };

  return undefined;
};

export const toSubjectsProps = (
  subjects: (
    | (Relation<'subject', SubjectAttributes> & SubjectLinkProps)
    | Data<SubjectAttributes>
  )[],
): SubjectProps[] => subjects.map((subject) => toSubjectProps(subject));

export const toSubjectGroupProps = (
  subjectGroup: SubjectGroup,
): SubjectGroupProps => {
  return {
    title: subjectGroup.title,
    description: subjectGroup.description,
    subjects: (subjectGroup.subjects || []).map((subjectRelation) =>
      toSubjectLinkProps(subjectRelation),
    ),
  };
};

export const toSubjectLinkProps = (
  subjectRelation: SubjectsRelation,
): SubjectLinkProps => {
  return {
    subject: toSubjectProps(subjectRelation.subject) || null,
    linkDescription: subjectRelation.linkDescription || null,
    linkExternal: subjectRelation.linkExternal || null,
    linkTitle: subjectRelation.linkTitle || null,
  };
};

export const toHomeProps = (
  homepageData: HomepageData,
): HomeProps | undefined => {
  return {
    title: homepageData?.title || '',
    subjectsTitle: homepageData?.subjectsTitle || '',
    subjects: toSubjectsProps(homepageData?.subjects || []),
    links: homepageData?.links,
    subjectGroups: (homepageData?.subjectGroups || []).map((group) =>
      toSubjectGroupProps(group),
    ),
  };
};

export const toMainPoint = (point: MainPointItem): MainPointProps => {
  if (point.__component === 'statistics.big-number-point') {
    return {
      type: 'bigNumber',
      color: point.color,
      text: point.text,
      value: point.value,
    };
  }
  if (point.__component === 'statistics.donut-point') {
    return {
      type: 'donut',
      color: point.color,
      text: point.text,
      value: point.value,
    };
  }
  if (point.__component === 'statistics.icon-point') {
    if (iconNames.includes(point.iconName)) {
      return {
        type: 'icon',
        color: point.color,
        icon: point.iconName,
        text: point.text,
      };
    }
  }
  if (point.__component === 'statistics.number-in-ten-point') {
    return {
      type: 'nummberInTen',
      color: point.color,
      text: point.text,
      value: point.value,
      variant: point.variant,
    };
  }
  return {
    type: 'notfound',
  };
};

export const createFakeResponse = (status: number, data: unknown) => {
  let statusText = 'Unknown Status';
  switch (status) {
    case 404:
      statusText = 'Not Found';
  }
  return {
    status,
    statusText,
    internal: true,
    data,
  };
};
